import { faqs, howItWorks } from "./helpers";
import { routes } from "@/global/routes";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

import type { TeamConfig } from "../types";

import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/MLS/Sounders/seattle-sounders-logo.png";
const FindGamesBanner =
  "https://ss-leagues.s3.amazonaws.com/MLS/Sounders/GroupBanner_1486x148.png";

const config: TeamConfig = {
  teamSchema: "seattlesounders_groups",
  browserTitle: "Sounders Groups",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Seattle Sounders FC",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00008-sounders-sec",
    mapId: "blockmap2d",

    // backend section codes -> 2d map node ids (node id without the "S_" prefix).
    // Codes that already match a node (100-151, 203-241, 306/309/312, S##, ECS) need no entry.
    sectionNameMapping: {
      // CHR (lower/upper club) -> physical section node
      "106": ["CHR106"],
      "107": ["CHR107"],
      "108": ["CHR108"],
      "109": ["CHR109"],
      "110": ["CHR110"],
      "111": ["CHR111"],
      "112": ["CHR112"],
      "132": ["CHR132"],
      "133": ["CHR133"],
      "134": ["CHR134"],
      "135": ["CHR135"],
      "136": ["CHR136"],
      "137": ["CHR137"],
      "138": ["CHR138"],
      "307": ["CHR307"],
      "308": ["CHR308"],
      "310": ["CHR310"],
      "311": ["CHR311"],
      // 309 and 312 exist both as plain and CHR codes -> same node, grouped
      "309": ["309", "CHR309"],
      "312": ["312", "CHR312"],

      // CLB (club level) -> physical section node
      "205": ["CLB205"],
      "206": ["CLB206"],
      "207": ["CLB207"],
      "208": ["CLB208"],
      "209": ["CLB209"],
      "210": ["CLB210"],
      "211": ["CLB211"],
      "212": ["CLB212"],
      "213": ["CLB213"],
      "232": ["CLB232"],
      "233": ["CLB233"],
      "234": ["CLB234"],
      "235": ["CLB235"],
      "236": ["CLB236"],
      "237": ["CLB237"],
      "238": ["CLB238"],
      "239": ["CLB239"],

      // C## (club suites) -> CS## nodes; CS48 is a single merged node for 48A/48B
      "CS12": ["C12"],
      "CS14": ["C14"],
      "CS25": ["C25"],
      "CS38": ["C38"],
      "CS48": ["C48A", "C48B"],
      "CS51": ["C51"],
      "CS52": ["C52"],
      "CS53": ["C53"],
      "CS54": ["C54"],
    },
    isGroupTicketSeatMapEnabled: true,
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.groupLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 10,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 10,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,

    isTicketTransferEnabled: true,
    hideViewTicketsInThankYouPage: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/soundersfc/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    authBackgroundImage: FindGamesBanner,
    findGamesBannerImage: FindGamesBanner,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    checkoutStatusPageImage: FindGamesBanner,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,
  },

  colors: {
    primary: "#0000FF",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarAditionalText: " to continue!",
      progressBarSuccessText:
        "You're good to go! Select Continue to view seating options.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Tickets will be available to be managed in your Seattle Sounders FC Account Manager within 24 hours.",

    gameFilters: {
      seatCounter: {
        description:
          "Select the number of people in your group. Minimum of 10 required.",
      },
    },

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
